import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Play,
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  Eye,
  MessageCircle,
  Database,
  Clock,
  BarChart3,
  X,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { createRoot } from 'react-dom/client';
import * as XLSX from 'xlsx';
import * as tus from 'tus-js-client';
import { api, API_BASE_URL } from './services/apiClient';
import { generatePDF } from './PDF_TEMPLATE';
import { logger } from './services/logger';

export { MOCK_TEMPLATES, TEMPLATE_STATUSES };

// Estados posibles para las plantillas
// Estados posibles para las plantillas
const TEMPLATE_STATUSES = [
  { label: 'En cola', color: 'bg-gray-200 text-gray-700' },
  { label: 'Procesando', color: 'bg-blue-100 text-blue-700' },
  { label: 'Completado', color: 'bg-green-100 text-green-700' },
  { label: 'Completado con advertencias', color: 'bg-yellow-100 text-yellow-700' },
  { label: 'Completado con errores', color: 'bg-warning-light text-warning-text' },
  { label: 'Error', color: 'bg-red-100 text-red-700' }
];

// Asignar un estado aleatorio a cada plantilla (solo para demo/mock)
const MOCK_TEMPLATES = [
  {
    id: 1,
    name: 'Catastro Urbano-001',
    description: 'Plantilla para catastro urbano con validaciones topológicas',
    validationCount: 45,
    createdAt: '2024-10-15',
    terminadoAt: '2024-10-16T11:00:00', // 1 día 2 horas después de inicio
    structure: {
      featureClasses: ['Predios', 'Construcciones', 'Vias'],
      tables: ['Propietarios', 'Avaluos'],
      domains: ['TipoPredio', 'EstadoConstruccion'],
      errorMessage: 'Error de procesamiento: No se pudo acceder a la GeoDatabase. Verifique la conexión y los permisos del archivo. Código de error: GDB-005.'
    },
    status: TEMPLATE_STATUSES[3]
  },
  {
    id: 2,
    name: 'Infraestructura Vial-002',
    description: 'Plantilla para gestión de infraestructura vial',
    validationCount: 32,
    createdAt: '2024-11-20',
    structure: {
      featureClasses: ['Vias', 'Puentes', 'Señalizacion'],
      tables: ['Mantenimiento', 'Inventario'],
      domains: ['TipoVia', 'EstadoVia']
    },
    status: TEMPLATE_STATUSES[1]
  },
  {
    id: 3,
    name: 'Servicios Públicos-003',
    description: 'Plantilla para redes de servicios públicos',
    validationCount: 38,
    createdAt: '2024-12-01',
    structure: {
      featureClasses: ['Alcantarillado', 'Acueducto', 'Energia'],
      tables: ['Usuarios', 'Medidores'],
      domains: ['TipoServicio', 'DiametroTuberia']
    },
    status: TEMPLATE_STATUSES[0]
  },
  {
    id: 4,
    name: 'Catastro Rural-004',
    description: 'Plantilla para catastro rural con reglas de integridad.',
    validationCount: 27,
    createdAt: '2024-09-10',
    terminadoAt: '2024-09-10T19:23:00', // 10 horas 23 min después de inicio
    structure: {
      featureClasses: ['Parcelas', 'Caminos'],
      tables: ['Propietarios'],
      domains: ['TipoParcela']
    },
    status: TEMPLATE_STATUSES[2] // Completado
  },
  {
    id: 5,
    name: 'Inventario de Árboles-005',
    description: 'Plantilla para inventario y monitoreo de árboles urbanos.',
    validationCount: 15,
    createdAt: '2024-08-22',
    terminadoAt: (() => {
      // Random entre 2 y 12 horas, y 0-59 minutos
      const h = Math.floor(Math.random() * 11) + 2;
      const m = Math.floor(Math.random() * 60);
      const date = new Date('2024-08-22T09:00:00');
      date.setHours(date.getHours() + h);
      date.setMinutes(date.getMinutes() + m);
      return date.toISOString().slice(0,16);
    })(),
    structure: {
      featureClasses: ['Árboles'],
      tables: ['Especies', 'Inspecciones'],
      domains: ['TipoEspecie', 'EstadoArbol']
    },
    status: TEMPLATE_STATUSES[2] // Completado
  },
  {
    id: 6,
    name: 'Red de Alumbrado Público-006',
    description: 'Plantilla para gestión de luminarias y postes.',
    validationCount: 21,
    createdAt: '2024-07-30',
    terminadoAt: (() => {
      // Random entre 1 y 15 horas, y 0-59 minutos
      const h = Math.floor(Math.random() * 15) + 1;
      const m = Math.floor(Math.random() * 60);
      const date = new Date('2024-07-30T09:00:00');
      date.setHours(date.getHours() + h);
      date.setMinutes(date.getMinutes() + m);
      return date.toISOString().slice(0,16);
    })(),
    structure: {
      featureClasses: ['Luminarias', 'Postes'],
      tables: ['Mantenimientos'],
      domains: ['TipoLuminaria', 'EstadoPoste']
    },
    status: TEMPLATE_STATUSES[2] // Completado
  },
];

// Simulación de validaciones por template
const MOCK_VALIDATIONS = {
  4: [
    {
      id: 1,
      name: 'Geometría válida',
      type: 'topological',
      description: 'Verificar que los polígonos de predios no tengan geometrías inválidas',
      feature: 'Parcelas',
      isActive: true,
      severity: 'error',
      rule: { operator: 'is_valid' }

    },
    {
      id: 2,
      name: 'Valores obligatorios',
      type: 'syntactic',
      description: 'Verificar que el código catastral esté presente y sea válido',
      feature: 'Parcelas',
      isActive: true,
      severity: 'error',
      rule: { operator: 'not_null' }
    },
    {
      id: 3,
      name: 'Dominios',
      type: 'domain',
      description: 'Verificar que el tipo de predio corresponda a valores del dominio',
      feature: 'Parcelas',
      isActive: true,
      severity: 'warning',
      rule: { operator: 'in_domain' }
    },
    {
      id: 4,
      name: 'Relación',
      type: 'relational',
      description: 'Verificar integridad referencial entre predios y propietarios',
      feature: 'Propietarios',
      isActive: false,
      severity: 'error',
      rule: { operator: 'foreign_key' }
    },
    {
      id: 5,
      name: 'Solapamiento',
      type: 'topological',
      description: 'Verificar que no existan solapamientos entre predios',
      feature: 'Parcelas',
      isActive: true,
      severity: 'error',
      rule: { operator: 'not_overlaps' }
    }
  ],
  5: [
    {
      id: 6,
      name: 'Conectividad - Red Vial',
      type: 'topological',
      description: 'Verificar conectividad en la red vial',
      feature: 'Vias',
      isActive: true,
      severity: 'error',
      rule: { operator: 'is_connected' }
    },
    {
      id: 7,
      name: 'Longitud mínima - Segmentos',
      type: 'syntactic',
      description: 'Verificar longitud mínima de segmentos viales',
      feature: 'Vias',
      isActive: true,
      severity: 'warning',
      rule: { operator: 'min_length' }
    }
  ],
  6: [
    {
      id: 8,
      name: 'Diámetro válido',
      type: 'domain',
      description: 'Verificar que el diámetro esté dentro de valores permitidos',
      feature: 'Alcantarillado',
      isActive: true,
      severity: 'error',
      rule: { operator: 'in_range' }
    }
  ]
};

const MOCK_RECORD_COUNTS = {
  'Parcelas': 1250,
  'Propietarios': 1100,
  'Vias': 850,
  'Alcantarillado': 2300
}

const MOCK_ERROR_MESSAGES = [
  "El campo CODIGO_PREDIO es obligatorio y no puede ser nulo.",
  "La geometría del polígono con ID 734 es inválida.",
  "El valor 'Urbano Residencial' no pertenece al dominio de tipos de predio.",
  "Se superó la longitud máxima de 50 caracteres en el campo PROPIETARIO.",
  "El área del predio es menor al mínimo permitido de 100 m².",
  "El código catastral '010101001' ya existe en la base de datos."
];

const MOCK_SUCCESS_MESSAGES = [
  "La geometría del polígono con ID 123 es válida.",
  "El campo CODIGO_PREDIO para el ID 456 no es nulo.",
  "El valor 'Urbano' para el ID 789 está dentro del dominio de tipos de predio.",
  "La longitud del campo PROPIETARIO es correcta.",
  "El área del predio con ID 555 cumple con el mínimo establecido."
];

const validationTypes = [
  { value: 'syntactic', label: 'Sintáctica' },
  { value: 'topological', label: 'Topológica' },
  { value: 'domain', label: 'De Dominio' },
  { value: 'relational', label: 'Relacional' }
];

const ValidationExecutionModule = ({
  useAuth,
  usePermissions,
  useNotification,
  useLoading,
  useExecution,
  BaseButton,
  BaseInput,
  BaseCard,
}) => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { addNotification: showNotification } = useNotification();
  const { showLoading, hideLoading } = useLoading();
  const { startTracking, pendingViewId, clearPendingView } = useExecution();

  // Estados principales
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResults, setExecutionResults] = useState(null);
  const [currentStep, setCurrentStep] = useState('template'); // template, file, execute, results
  const [newExecutionTemplateSearch, setNewExecutionTemplateSearch] = useState("");
  const [showTemplateOptions, setShowTemplateOptions] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [validationComments, setValidationComments] = useState({});
  const [templateSearch, setTemplateSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedValidationId, setExpandedValidationId] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({
    structure: true,
    syntactic: true,
    domain: true,
    relational: true,
    topological: true
  });
  const [activeFilters, setActiveFilters] = useState({}); // { syntactic: { passed: true, failed: true, error: true, warning: true, info: true }, ... }
  const [operatorsByType, setOperatorsByType] = useState({}); // Operadores por tipo desde BD
  const [operatorsLoading, setOperatorsLoading] = useState(true);
  const [detailedResult, setDetailedResult] = useState(null);
  const [errorModalContent, setErrorModalContent] = useState(null);
  const [showValidationDetailsModal, setShowValidationDetailsModal] = useState(false);
  const [validationToShowDetails, setValidationToShowDetails] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [executionsLoading, setExecutionsLoading] = useState(true);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [templateValidationCounts, setTemplateValidationCounts] = useState(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const [hallazgoSortDirection, setHallazgoSortDirection] = useState('asc'); // 'asc' o 'desc'
  const [tusUploadId, setTusUploadId] = useState(null);      // ID del upload tus completado
  const [uploadProgress, setUploadProgress] = useState(0);   // 0-100
  const [isUploading, setIsUploading] = useState(false);     // true mientras sube el archivo
  const [isDragging, setIsDragging] = useState(false);       // resaltado visual al arrastrar
  const fileInputRef = useRef(null);
  const pollingRef = useRef(null);
  const tusUploadRef = useRef(null);                         // instancia tus.Upload activa

  // Verificar permisos
  const canExecute = hasPermission('execute_validations');
  const canViewResults = hasPermission('execute_validations');

  const getStatusDisplay = (status) => {
    const map = {
      'queued':                 { label: 'En cola',                    color: 'bg-gray-200 text-gray-700' },
      'pending':                { label: 'En cola',                    color: 'bg-gray-200 text-gray-700' },
      'running':                { label: 'Procesando',                 color: 'bg-blue-100 text-blue-700' },
      'completed':              { label: 'Completado',                 color: 'bg-green-100 text-green-700' },
      'completedwithwarns':  { label: 'Completado con advertencias', color: 'bg-yellow-100 text-yellow-700' },
      'completedwitherrors':    { label: 'Completado con errores',     color: 'bg-warning-light text-warning-text' },
      'failed':                 { label: 'Error',                      color: 'bg-red-100 text-red-700' },
      'error':                  { label: 'Error',                      color: 'bg-red-100 text-red-700' },
    };
    return map[(status || '').toLowerCase()] || { label: status || 'Desconocido', color: 'bg-gray-200 text-gray-700' };
  };

  const normalizeExecution = (exec) => ({
    id: exec.id,
    name: exec.templateName || `Ejecución #${exec.id}`,
    description: exec.gdbFilePath ? `GDB: ${exec.gdbFilePath.split(/[\\/]/).pop()}` : `Plantilla ID ${exec.templateId}`,
    validationCount: exec.totalRules || 0,
    createdAt: exec.startedAt || new Date().toISOString(),
    terminadoAt: exec.completedAt || null,
    status: getStatusDisplay(exec.status),
    // Origen de la ejecución: "web_upload" (sube GDB) o "addin_sde"/"addin_gdb" (Add-in)
    sourceType: exec.sourceType || 'web_upload',
    structure: {
      featureClasses: Array(exec.featureClassCount || 0).fill(null),
      tables: Array(exec.tableCount || 0).fill(null),
      domains: Array(exec.domainCount || 0).fill(null),
      errorMessage: (exec.errors || []).join('; '),
    },
    _raw: exec,
  });

  const loadExecutions = async () => {
    setExecutionsLoading(true);
    try {
      const data = await api.get('/api/validationexecutions');
      setExecutions((data || []).map(normalizeExecution));
    } catch (error) {
      logger.error('Error loading executions:', { error });
      setExecutions(MOCK_TEMPLATES);
    } finally {
      setExecutionsLoading(false);
    }
  };

  const loadAvailableTemplates = async () => {
    try {
      const data = await api.get('/api/templates');
      const all = data || MOCK_TEMPLATES;
      const filtered = user?.role === 'admin'
        ? all
        : all.filter(t => t.createdBy === user?.username);
      setAvailableTemplates(filtered);
    } catch (error) {
      logger.error('Error loading templates:', { error });
      setAvailableTemplates(MOCK_TEMPLATES);
    }
  };

  const loadTemplateValidationCounts = async (templateId) => {
    logger.debug('loadTemplateValidationCounts llamado con templateId:', { templateId });
    try {
      const validations = await api.get(`/api/validation/template/${templateId}`);
      logger.debug('Respuesta del API /api/validation/template/', { validations });
      const activeValidations = (validations || []).filter(v => v.isActive);

      const counts = {
        structure:   activeValidations.filter(v => v.type === 'structure').length,
        syntactic:   activeValidations.filter(v => v.type === 'syntactic').length,
        domain:      activeValidations.filter(v => v.type === 'domain').length,
        relational:  activeValidations.filter(v => v.type === 'relational').length,
        topological: activeValidations.filter(v => v.type === 'topological').length,
      };

      setTemplateValidationCounts(counts);
    } catch (error) {
      logger.error('Error loading validation counts:', { error });
      setTemplateValidationCounts(null);
    }
  };

  const handleViewRealExecution = async (exec) => {
    try {
      const id = exec._raw?.id ?? exec.id ?? exec;
      const summary = await api.get(`/api/validationexecutions/${id}/summary`);
      const resultsData = await api.get(`/api/validationexecutions/${id}/results`);
      const executionSummary = {
        id: summary.id,
        templateName: summary.templateName,
        fileName: summary.gdbFilePath ? summary.gdbFilePath.split(/[\\/]/).pop() : '',
        executedAt: summary.completedAt || summary.startedAt,
        totalValidations: summary.totalRules || 0,
        passedValidations: summary.passedRules || 0,
        failedErrorValidations: summary.failedErrorRules || 0,
        failedWarningValidations: summary.failedWarningRules || 0,
        failedInfoValidations: summary.failedInfoRules || 0,
        totalRecordsChecked: summary.executedRules || 0,
        totalErrorsFound: (summary.failedErrorRules || 0) + (summary.failedWarningRules || 0) + (summary.failedInfoRules || 0),
        results: Array.isArray(resultsData) ? resultsData : resultsData?.results || [],
      };
      setExecutionResults(executionSummary);
      setSelectedTemplate({ id: summary.templateId, name: summary.templateName });
      setCurrentStep('results');
      setDetailedResult(null);
      setExpandedValidationId(null);
      setValidationComments({});
    } catch (error) {
      showNotification('Error al cargar resultados de la ejecución', 'error');
    }
  };

  const clearFilters = () => {
    setTemplateSearch("");
    setStartDate("");
    setEndDate("");
    setStatusFilter("all");
  };

  // Toggle de grupo de validación por tipo
  const toggleGroup = (type) => {
    setExpandedGroups(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleBackToTemplateList = () => {
    setExecutionResults(null);
    setCurrentStep('template');
  };

  useEffect(() => {
    if (pendingViewId) {
      clearPendingView();
      handleViewRealExecution({ id: pendingViewId, _raw: { id: pendingViewId } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingViewId]);

  useEffect(() => {
    if (!canExecute) {
      showNotification('No tienes permisos para ejecutar validaciones', 'error');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canExecute]);

  useEffect(() => {
    loadExecutions();
    loadAvailableTemplates();
    loadOperators();
  }, []);

  useEffect(() => {
    const handleExecutionCompleted = () => loadExecutions();
    window.addEventListener('valida:executionCompleted', handleExecutionCompleted);
    return () => window.removeEventListener('valida:executionCompleted', handleExecutionCompleted);
  }, []);

  // Cargar operadores por tipo desde la BD (con cacheo)
  const loadOperators = async () => {
    // Si ya está cargado, no hacer nada
    if (Object.keys(operatorsByType).length > 0) {
      setOperatorsLoading(false);
      return;
    }
    
    const types = ['structure', 'syntactic', 'domain', 'relational', 'topological'];
    const operatorsData = { ...operatorsByType }; // Mantiene lo ya cargado
    
    for (const type of types) {
      // Solo llama si no está cargado
      if (!operatorsData[type] || operatorsData[type].length === 0) {
        try {
          const response = await api.get(`/api/validationmetadata/operators/${type}`);
          operatorsData[type] = response || [];  // response tiene los datos directamente
        } catch (error) {
          logger.warn(`Error loading operators for type ${type}:`, { error });
          operatorsData[type] = [];
        }
      }
    }
    
    setOperatorsByType(operatorsData);
    setOperatorsLoading(false);
  };

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // Advertencia beforeunload mientras el archivo se está subiendo
  useEffect(() => {
    if (!isUploading) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = 'El archivo se está subiendo al servidor. Si sale ahora, podrá retomar seleccionando el mismo archivo.';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isUploading]);

  // Memoización para búsqueda rápida de operadores (O(1) en lugar de O(n))
  const operatorsLookup = useMemo(() => {
    logger.debug('operatorsLookup operatorsByType', { operatorsByType });
    
    const lookup = {};
    Object.entries(operatorsByType).forEach(([type, operators]) => {
      if (!operators || !Array.isArray(operators)) {
        logger.warn(`operatorsLookup: operators for type ${type} is not an array`, { operators });
        return;
      }
      
      lookup[type] = {};
      operators.forEach((op, index) => {
        // Soportar tanto camelCase (JavaScript) como PascalCase (C# JSON)
        const label = op.label || op.Label;
        const value = op.value || op.Value;
        
        if (!value) {
          logger.warn(`operatorsLookup: operator at index ${index} has no value`, { op });
          return;
        }
        
        if (!label) {
          logger.warn(`operatorsLookup: operator ${value} has no label`, { op });
          return;
        }
        
        lookup[type][value] = label;
      });
    });
    
    logger.debug('operatorsLookup: built lookup', { lookup });
    return lookup;
  }, [operatorsByType]);

  // Manejar selección de template
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setCurrentStep('file');
    setExecutionResults(null);
    setValidationComments({});
    setExpandedValidationId(null);
    setShowTemplateOptions(false);
    setNewExecutionTemplateSearch("");
    loadTemplateValidationCounts(template.id);
  };

  const showErrorModal = (template) => {
    setErrorModalContent(template.structure?.errorMessage || 'Ocurrió un error inesperado durante la ejecución.');
  };

  const handleViewResults = (template) => {
    const templateValidations = (MOCK_VALIDATIONS[template.id] || []).map(v => ({
      ...v,
      // name: v.name.includes(' - ') ? v.name : `${v.type.charAt(0).toUpperCase() + v.type.slice(1)} - ${v.name}`
      name: v.type.charAt(0).toUpperCase() + v.type.slice(1)
    }));

    const activeValidations = templateValidations.filter(v => v.isActive);

    const results = activeValidations.map((validation, index) => {
      // Usar IDs para generar resultados deterministas en lugar de aleatorios
      const seed = template.id + validation.id + index;
      const pseudoRandom = parseFloat('0.' + Math.sin(seed).toString().substr(6));
      const passed = pseudoRandom > 0.3; // 70% de probabilidad de pasar
      const recordsChecked = MOCK_RECORD_COUNTS[validation.feature] || 0;
      const errorsFound = passed ? 0 : 1 + (seed % 50);

      let hallazgos = [];
      const numExamples = Math.floor(Math.random() * 3) + 3; // 3 a 5 ejemplos
      for (let i = 0; i < numExamples; i++) {
        const isSuccessExample = Math.random() > 0.5;

        hallazgos.push({
          feature: 'Predios',
          field: validation.name.split(' - ')[1] || 'N/A',
          id: Math.floor(Math.random() * 10000),
          resultMessage: isSuccessExample
            ? MOCK_SUCCESS_MESSAGES[Math.floor(Math.random() * MOCK_SUCCESS_MESSAGES.length)]
            : MOCK_ERROR_MESSAGES[Math.floor(Math.random() * MOCK_ERROR_MESSAGES.length)],
          isSuccess: isSuccessExample
        });
      }

      return {
        ...validation,
        recordsChecked: hallazgos.length,
        errorsFound: hallazgos.filter(e => !e.isSuccess).length,
        executedAt: new Date(template.terminadoAt || template.createdAt).toISOString(),
        message: generateValidationMessage(hallazgos),
        hallazgos
      };
    });

    

    const executionSummary = {
        templateName: template.name,
        fileName: `archivo_ejecutado_${template.id}.gdb`, // Nombre de archivo simulado
        executedAt: new Date(template.terminadoAt || template.createdAt).toISOString(),
        totalValidations: results.reduce((sum, r) => sum + (r.hallazgos?.length || 0), 0),
        passedValidations: results.reduce((sum, r) => sum + (r.hallazgos?.filter(ex => ex.isSuccess).length || 0), 0),
        failedErrorValidations: results.reduce((sum, r) => sum + (r.hallazgos?.filter(ex => !ex.isSuccess && r.severity === 'error')?.length || 0), 0),
        failedWarningValidations: results.reduce((sum, r) => sum + (r.hallazgos?.filter(ex => !ex.isSuccess && r.severity === 'warning')?.length || 0), 0),
        failedInfoValidations: results.reduce((sum, r) => sum + (r.hallazgos?.filter(ex => !ex.isSuccess && r.severity === 'info')?.length || 0), 0),
        totalRecordsChecked: results.reduce((sum, r) => sum + r.recordsChecked, 0),
        totalErrorsFound: results.reduce((sum, r) => sum + r.errorsFound, 0),
        results
    };

    setExecutionResults(executionSummary);
    setSelectedTemplate(template);
    setCurrentStep('results');
    setDetailedResult(null);
    setExpandedValidationId(null);
  };
  
  const handleTemplateNew = () => {
    setCurrentStep('new');
    setSelectedTemplate(null);
    setUploadedFile(null);
    setTusUploadId(null);
    setUploadProgress(0);
    setIsUploading(false);
    setNewExecutionTemplateSearch("");
    setTemplateValidationCounts(null);
  };

  // Función para decodificar el JSON de FieldValue y devolver campos estructurados
  const decodeHallazgoFieldValue = (fieldValue, validationType) => {
    // Primero, parsear el string JSON si es necesario
    let parsedValue = fieldValue;
    if (typeof fieldValue === 'string') {
      try {
        parsedValue = JSON.parse(fieldValue);
      } catch (e) {
        // Si no es JSON válido, retornar como string
        return { type: 'unknown', fields: [{ label: 'Valor', value: fieldValue }] };
      }
    }
    
    if (!parsedValue || typeof parsedValue !== 'object') {
      return { type: 'unknown', fields: [{ label: 'Valor', value: parsedValue }] };
    }
    
    // Mostrar TODOS los campos del JSON sin filtrar
    const fields = Object.entries(parsedValue).map(([key, value]) => {
      // Formatear label: camelCase o snake_case → "Camel Case" en español
      let label = key
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/^./, str => str.toUpperCase());

      // Traducir labels comunes al español
      const labelTranslations = {
        // Campos genéricos
        'Razon': 'Razón',
        'Reason': 'Razón',
        'Field Name': 'Campo',
        'Field Value': 'Valor en el campo',
        'fieldName': 'Campo',
        'fieldValue': 'Valor en el campo',
        // Dominio
        'Domain Name': 'Nombre del dominio',
        'domainName': 'Nombre del dominio',
        // Rango numérico
        'Min Value': 'Valor mínimo permitido',
        'Max Value': 'Valor máximo permitido',
        'minValue': 'Valor mínimo permitido',
        'maxValue': 'Valor máximo permitido',
        // Longitudes
        'Max Length': 'Longitud máxima',
        'Min Length': 'Longitud mínima',
        'Actual Length': 'Longitud actual',
        'maxLength': 'Longitud máxima',
        'minLength': 'Longitud mínima',
        'actualLength': 'Longitud actual',
        // Tipos
        'Expected Type': 'Tipo esperado',
        'Actual Type': 'Tipo actual',
        'expectedType': 'Tipo esperado',
        'actualType': 'Tipo actual',
        // Precisión
        'Max Precision': 'Precisión máxima',
        'Actual Precision': 'Precisión actual',
        'maxPrecision': 'Precisión máxima',
        'actualPrecision': 'Precisión actual',
        // Fechas
        'Min Date': 'Fecha mínima',
        'Max Date': 'Fecha máxima',
        'Actual Date': 'Fecha en el campo',
        'minDate': 'Fecha mínima',
        'maxDate': 'Fecha máxima',
        'actualDate': 'Fecha en el campo',
        // Regex
        'Pattern': 'Patrón regex',
        'pattern': 'Patrón regex',
        // Longitud exacta
        'Exact Length': 'Longitud exacta requerida',
        'exactLength': 'Longitud exacta requerida',
        // Valores prohibidos
        'Not Allowed Values': 'Valores no permitidos',
        'notAllowedValues': 'Valores no permitidos',
        // Duplicados
        'First Occurrence Fid': 'Primera ocurrencia (FID)',
        'firstOccurrenceFid': 'Primera ocurrencia (FID)',
        // Relacional
        'Related Table': 'Tabla relacionada',
        'Expected Range': 'Rango esperado',
        'relatedTable': 'Tabla relacionada',
        'expectedRange': 'Rango esperado',
        // Topológico
        'Violation Type': 'Tipo de violación',
        'Area': 'Área',
        'Overlap Area': 'Área de solapamiento',
        'Unit': 'Unidad',
        'Related Feature Id': 'Feature relacionado',
        'Error Detail': 'Detalle del error',
        'violationType': 'Tipo de violación',
        'area': 'Área',
        'overlapArea': 'Área de solapamiento',
        'unit': 'Unidad',
        'relatedFeatureId': 'Feature relacionado',
        'errorDetail': 'Detalle del error',
        // CRS / Spatial (claves en inglés — formato legacy)
        'Metodo Comparacion': 'Método de comparación',
        'Codigo Srid Esperado': 'Código SRID esperado',
        'Codigo Srid Actual': 'Código SRID actual',
        'Nombre Crs Esperado': 'Nombre CRS esperado',
        'Nombre Crs Actual': 'Nombre CRS actual',
        'Nombre Crs Completo Esperado': 'Nombre completo CRS esperado',
        'Nombre Crs Completo Actual': 'Nombre completo CRS actual',
        'Comparison Method': 'Método de comparación',
        'Expected Srid Code': 'Código SRID esperado',
        'Actual Srid Code': 'Código SRID actual',
        'Expected Crs Name': 'Nombre CRS esperado',
        'Actual Crs Name': 'Nombre CRS actual',
        'Expected Crs Full Name': 'Nombre completo CRS esperado',
        'Actual Crs Full Name': 'Nombre completo CRS actual',
        // CRS / Spatial (claves en español — formato actual del backend)
        'Crs Name Esperado': 'Nombre CRS esperado',
        'Crs Name Actual': 'Nombre CRS actual',
        'Canonical Name Esperado': 'Nombre canónico esperado',
        'Canonical Name Actual': 'Nombre canónico actual',
        'Wkt Esperado': 'WKT esperado',
        'Wkt Actual': 'WKT actual',
        'Mapeo Utilizado': 'Método de mapeo',
        // geometry_type
        'Geometry Type Esperado': 'Tipo de geometría esperado',
        'Geometry Type Actual': 'Tipo de geometría actual',
        'Geometry Type Esperado Enum': 'Código del tipo esperado',
        'Geometry Type Actual Enum': 'Código del tipo actual',
        'Es Compatible': '¿Es compatible?',
        'Nivel Compatibilidad': 'Nivel de compatibilidad',
        'Resultado Validacion': 'Resultado de la validación',
        'Advertencia': 'Advertencia',
        // feature_required
        'Feature Count': 'Cantidad de features',
        // Dependencias
        'Dependency Type': 'Tipo de Dependencia',
        'Blocked Rule Name': 'Nombre de Regla Bloqueada',
        'Blocked Rule Operator': 'Operador de Regla Bloqueada',
        'Blocked Rule Type': 'Tipo de Regla Bloqueada',
        'Failed Dependencies': 'Dependencias Fallidas',
        'Message': 'Mensaje',
        'Blocked At': 'Fecha de Bloqueo'
      };

      // Traducir valores de la clave "reason"
      const reasonTranslations = {
        'not_in_domain':                   'El valor no pertenece al dominio configurado',
        'out_of_range':                    'El valor está fuera del rango permitido',
        'null_value':                      'Campo requerido sin valor',
        'max_length_exceeded':             'El valor supera la longitud máxima',
        'min_length_not_met':              'El valor no alcanza la longitud mínima',
        'regex_pattern_not_matched':       'El valor no coincide con el patrón',
        'numeric_precision_exceeded':      'La precisión numérica supera el máximo',
        'date_unparseable':                'La fecha no pudo ser interpretada',
        'date_out_of_range':               'La fecha está fuera del rango',
        'data_type_mismatch':              'El tipo de dato no coincide con el esperado',
        'duplicate_value':                 'Valor duplicado encontrado',
        'missing_field':                   'Campo requerido no existe en la capa',
        'field_type_mismatch':             'El tipo del campo no coincide',
        'extra_field':                     'Campo extra no definido en la plantilla',
        'foreign_key_violation':           'El valor no existe en la tabla relacionada',
        'value_not_found':                 'Valor no encontrado en la capa de referencia',
        'cardinality_violation':           'La cardinalidad de la relación fue violada',
        'invalid_geometry':                'La geometría es inválida',
        'invalid_geometry_linearization':  'Error al linearizar la geometría',
      };

      if (labelTranslations[label]) {
        label = labelTranslations[label];
      }

      // Formatear valor
      let formattedValue = value;
      let renderAs = 'text';

      // Traducir valores de la clave "reason" (inglés o español)
      if ((key === 'reason' || key === 'razon') && typeof value === 'string' && reasonTranslations[value]) {
        formattedValue = reasonTranslations[value];
      } else if (value === null || value === undefined || value === '') {
        formattedValue = '(vacío)';
      } else if (typeof value === 'boolean') {
        formattedValue = value ? 'Sí' : 'No';
      } else if (Array.isArray(value)) {
        // Si es array de objetos, convertir a string legible
        if (value.length > 0 && typeof value[0] === 'object') {
          // Para failedDependencies, mostrar como lista de nombres
          if (key === 'failedDependencies') {
            formattedValue = value.map(dep => {
              const depName = dep.dependencyRuleName || dep.dependencyRuleName || 'Unknown';
              const depType = dep.dependencyRuleType || dep.dependencyRuleType || '';
              return `${depName} (${depType})`;
            }).join(', ');
          } else {
            // Otros arrays de objetos -> JSON string
            formattedValue = value.map(item => JSON.stringify(item)).join(', ');
          }
        } else {
          // Array de valores simples
          formattedValue = value.join(', ');
        }
      } else if (typeof value === 'object') {
        formattedValue = JSON.stringify(value, null, 2);
      }

      return {
        _key: key,
        label: label,
        value: formattedValue,
        renderAs: renderAs
      };
    });

    // Ordenar: primero campo/valor, contexto, razón al final
    const fieldOrder = [
      // Identificación primaria
      'fieldName', 'fieldValue', 'featureCount',
      // Contexto de dominio / rango
      'domainName', 'minValue', 'maxValue',
      // Tipos de datos
      'expectedType', 'actualType',
      // CRS / Geometría
      'crsNameEsperado', 'crsNameActual',
      'canonicalNameEsperado', 'canonicalNameActual',
      'geometryTypeEsperado', 'geometryTypeActual',
      // Longitudes / precisión
      'minLength', 'maxLength', 'actualLength',
      'maxPrecision', 'actualPrecision',
      // Enums / compatibilidad
      'geometryTypeEsperadoEnum', 'geometryTypeActualEnum',
      'esCompatible', 'nivelCompatibilidad',
      // Regex / fechas
      'pattern',
      'minDate', 'maxDate', 'actualDate',
      // Relacional
      'relatedTable', 'firstOccurrenceFid', 'expectedRange',
      // Topológico
      'violationType', 'area', 'overlapArea', 'unit', 'relatedFeatureId',
      // Método / mapeo
      'metodoComparacion', 'mapeoUtilizado',
      // WKT (verbose — al final para no obstruir la lectura)
      'wktEsperado', 'wktActual',
      // Meta
      'resultadoValidacion', 'advertencia', 'errorDetail',
      // Razón siempre al final (inglés y español)
      'reason', 'razon',
    ];
    fields.sort((a, b) => {
      const ia = fieldOrder.indexOf(a._key);
      const ib = fieldOrder.indexOf(b._key);
      return (ia === -1 ? fieldOrder.length : ia) - (ib === -1 ? fieldOrder.length : ib);
    });

    return {
      type: validationType || 'unknown',
      fields: fields
    };
  };

  // Función para manejar sorting de hallazgos por ID
  const handleHallazgoSort = () => {
    setHallazgoSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  // Función para ordenar hallazgos
  const sortHallazgos = (hallazgos) => {
    if (!hallazgos) return [];
    return [...hallazgos].sort((a, b) => {
      const idA = a.id || 0;
      const idB = b.id || 0;
      return hallazgoSortDirection === 'asc' ? idA - idB : idB - idA;
    });
  };

  // Función auxiliar para renderizar badges
  const renderBadges = (values) => {
    if (!Array.isArray(values)) return String(values);
    return (
      <div className="flex flex-wrap gap-1">
        {values.map((val, i) => (
          <span key={i} className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-700">
            {typeof val === 'object' ? JSON.stringify(val) : String(val)}
          </span>
        ))}
      </div>
    );
  };

  const handleShowValidationDetails = (validation, hallazgo = null) => {
    // Combinar datos del resultado con datos del hallazgo específico
    const detailsData = {
      ...validation,
      // Datos específicos del hallazgo si se proporcionaron
      hallazgo: hallazgo
    };
    
    setValidationToShowDetails(detailsData);
    setShowValidationDetailsModal(true);
  };

  const handleSaveComments = async (validationId) => {
    const commentObj = validationComments[validationId];
    const content = commentObj?.content ?? commentObj ?? '';
    if (!content || content.trim() === '') {
      showNotification('No hay comentarios para guardar en esta validación.', 'warning');
      return;
    }

    try {
      const saved = await api.post('/api/validation-comments', {
        ValidationResultId: parseInt(validationId),
        Content: content,
        CreatedBy: user?.username || user?.name || ''
      });

      setValidationComments(prev => ({
        ...prev,
        [validationId]: {
          content,
          createdBy: saved?.createdBy || user?.username || user?.name || '',
          createdAt: saved?.createdAt || new Date().toISOString()
        }
      }));

      showNotification('Comentario guardado exitosamente', 'success');

    } catch (error) {
      logger.error('Error saving comment:', { error });
      showNotification('Error al guardar el comentario', 'error');
    }
  };

  // Manejar carga de archivo — inicia upload tus inmediatamente al seleccionar
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.zip')) {
      showNotification('Por favor selecciona un archivo .zip válido', 'error');
      return;
    }

    // Cancelar upload anterior si existía
    if (tusUploadRef.current) {
      tusUploadRef.current.abort();
      tusUploadRef.current = null;
    }

    setUploadedFile(file);
    setTusUploadId(null);
    setUploadProgress(0);
    setIsUploading(true);

    const token = localStorage.getItem('authToken');
    const upload = new tus.Upload(file, {
      endpoint: `${API_BASE_URL()}/files/`,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      chunkSize: 50 * 1024 * 1024, // 50 MB por chunk
      metadata: {
        filename: file.name,
        filetype: file.type || 'application/octet-stream',
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
      onError: (error) => {
        logger.error('Error en upload tus:', { error });
        setIsUploading(false);
        showNotification('Error al subir el archivo. Verifique su conexión e intente nuevamente.', 'error');
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        setUploadProgress(Math.round((bytesUploaded / bytesTotal) * 100));
      },
      onSuccess: () => {
        const uploadId = upload.url.split('/').pop();
        setTusUploadId(uploadId);
        setIsUploading(false);
        setUploadProgress(100);
        showNotification('Archivo subido correctamente. Ya puede ejecutar la validación.', 'success');
        if (currentStep === 'file') setCurrentStep('execute');
      },
    });

    // Detectar upload anterior en localStorage para reanudar
    upload.findPreviousUploads().then((previous) => {
      if (previous.length > 0) {
        upload.resumeFromPreviousUpload(previous[0]);
        showNotification('Retomando subida anterior...', 'info');
      }
      upload.start();
    });

    tusUploadRef.current = upload;
  };

  // Disparar el input file desde el botón
  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  // Drag & drop handlers para el área de carga
  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload({ target: { files: [file] } });
  };

  // Ejecutar validaciones — el archivo ya está subido vía tus, solo se envía el ID
  const executeValidations = async () => {
    if (!selectedTemplate) {
      showNotification('Debe seleccionar una plantilla', 'error');
      return;
    }
    if (isUploading) {
      showNotification('El archivo todavía se está subiendo. Espere a que llegue al 100%.', 'error');
      return;
    }
    if (!tusUploadId) {
      showNotification('Debe seleccionar y subir un archivo GeoDatabase', 'error');
      return;
    }

    setIsExecuting(true);
    showLoading('Iniciando validación...');

    try {
      const response = await api.post('/api/validationexecutions/start', {
        templateId: selectedTemplate.id,
        tusUploadId,
      });
      const executionId = response.executionId;

      hideLoading();
      setIsExecuting(false);
      showNotification('Validación iniciada. Procesando en segundo plano...', 'info');

      // Delegar seguimiento al contexto global
      startTracking(executionId, selectedTemplate.name);

      // Navegar al listado para que el usuario vea su ejecución en la lista
      await loadExecutions();
      setCurrentStep('template');

    } catch (error) {
      showNotification('Error al iniciar validaciones: ' + error.message, 'error');
      setIsExecuting(false);
      hideLoading();
    }
  };

  // Generar mensaje de validación
  const generateValidationMessage = (hallazgos) => {
    const successfulCount = hallazgos.filter(e => e.isSuccess).length;
    const failedCount = hallazgos.filter(e => !e.isSuccess).length;
    return `✅ Validaciones superadas: ${successfulCount} ❌ Validaciones no superadas: ${failedCount}`;
  };

  // Generar reporte
  const generateReport = async () => {
    const commentPromises = executionResults.results.map(r =>
      api.get(`/api/validation-comments/result/${r.id}`)
        .then(comments => ({ id: r.id, comment: comments?.[0] || null }))
        .catch(() => ({ id: r.id, comment: null }))
    );
    const allComments = await Promise.all(commentPromises);
    const commentsMap = { ...validationComments };
    allComments.forEach(c => {
      if (c.comment) commentsMap[c.id] = {
        content: c.comment.content,
        createdBy: c.comment.createdBy,
        createdAt: c.comment.createdAt
      };
    });
    setValidationComments(commentsMap);
    setShowReport(true);
  };

  // Exportar resultados a PDF — el backend genera el PDF con branding del tenant
  const exportResults = async () => {
    if (!executionResults?.id) {
      showNotification('No hay resultados para exportar.', 'error');
      return;
    }

    setIsPdfLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL()}/api/reports/executions/${executionResults.id}/pdf`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } }
      );
      if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);

      const blob = await response.blob();
      const safeBase = (executionResults.fileName || 'validacion')
        .replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40);
      const datePart = new Date().toISOString().slice(0, 10);

      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `Informe_Validacion_${safeBase}_${datePart}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showNotification('Reporte PDF descargado exitosamente', 'success');
    } catch (error) {
      logger.error('Error al generar el PDF:', { error });
      showNotification('Error al generar el reporte PDF', 'error');
    } finally {
      setIsPdfLoading(false);
    }
  };

  // Exportar resultados a Excel — sábana de datos 100% client-side con SheetJS
  const exportToExcel = async () => {
    if (!executionResults) {
      showNotification('No hay resultados para exportar.', 'error');
      return;
    }

    setIsExcelLoading(true);
    showNotification('Generando Excel...', 'info');

    try {
      // Obtener resumen completo de la ejecución (tiene ExecutedBy, Status, ExecutionTimeMs, etc.)
      const summary = await api.get(`/api/validationexecutions/${executionResults.id}/summary`);
      const results = executionResults.results || [];

      // ── 1. Recopilar todos los keys dinámicos del fieldValue de todos los hallazgos ──
      const dynamicKeysSet = new Set();
      results.forEach(result => {
        (result.hallazgos || []).forEach(h => {
          let fv = h.fieldValue;
          if (typeof fv === 'string') { try { fv = JSON.parse(fv); } catch { fv = null; } }
          if (fv && typeof fv === 'object' && !Array.isArray(fv)) {
            Object.keys(fv).forEach(k => dynamicKeysSet.add(k));
          }
        });
      });
      const dynamicCols = Array.from(dynamicKeysSet);

      // ── 2. Helpers ──
      const formatDate = (d) => {
        if (!d) return '';
        const dt = new Date(d);
        return isNaN(dt.getTime()) ? String(d) : dt.toLocaleString('es-CO');
      };
      const safeStr = (v) => (v === null || v === undefined) ? '' : String(v);
      const execTimeSeg = summary.executionTimeMs > 0
        ? (summary.executionTimeMs / 1000).toFixed(2)
        : '';

      // ── 3. Construir filas planas ──
      const rows = [];
      results.forEach(result => {
        const hallazgos = result.hallazgos || [];

        // Datos de la ejecución — se repiten en cada fila
        const execCols = {
          'ID Ejecución':            summary.id,
          'Plantilla':               safeStr(summary.templateName),
          'Archivo GDB':             safeStr(executionResults.fileName),
          'Ejecutado Por':           safeStr(summary.executedBy || summary.userId),
          'Estado Ejecución':        safeStr(summary.status),
          'Fecha Inicio':            formatDate(summary.startedAt),
          'Fecha Fin':               formatDate(summary.completedAt),
          'Tiempo Ejecución (seg)':  execTimeSeg,
          'Total Validaciones':      summary.totalRules || 0,
          'Reglas Ejecutadas':       summary.executedRules || 0,
          'Superadas':               summary.passedRules || 0,
          'Fallidas (Error)':        summary.failedErrorRules || 0,
          'Fallidas (Advertencia)':  summary.failedWarningRules || 0,
          'Fallidas (Info)':         summary.failedInfoRules || 0,
        };

        // Datos de la validación — se repiten por hallazgo
        const valCols = {
          'ID Validación':      result.id,
          'Nombre Regla':       safeStr(result.name),
          'Tipo':               safeStr(result.type),
          'Operador':           safeStr(result.rule?.operator),
          'Severidad':          safeStr(result.severity),
          'Estado Validación':  (result.isPassed ?? (result.hallazgos?.length ?? 0) === 0) ? 'SUPERADA' : 'NO SUPERADA',
          'Mensaje':            safeStr(result.message),
        };

        if (hallazgos.length === 0) {
          // Sin hallazgos → una fila con hallazgo vacío
          const row = { ...execCols, ...valCols,
            '# Hallazgo':    '',
            'ID Feature':    '',
            'Nombre Feature': '',
            'Campo':         '',
          };
          dynamicCols.forEach(k => { row[k] = ''; });
          rows.push(row);
        } else {
          hallazgos.forEach((h, idx) => {
            let fv = h.fieldValue;
            if (typeof fv === 'string') { try { fv = JSON.parse(fv); } catch { fv = null; } }

            const row = {
              ...execCols,
              ...valCols,
              '# Hallazgo':     idx + 1,
              'ID Feature':     h.id ?? '',
              'Nombre Feature': safeStr(h.feature),
              'Campo':          safeStr(h.field),
            };

            // Columnas dinámicas del fieldValue
            dynamicCols.forEach(k => {
              const val = fv?.[k];
              if (val === undefined)          row[k] = '';
              else if (val === null)          row[k] = 'NULL';
              else if (Array.isArray(val))    row[k] = val.join(', ');
              else if (typeof val === 'object') row[k] = JSON.stringify(val);
              else                            row[k] = val;
            });

            rows.push(row);
          });
        }
      });

      if (rows.length === 0) {
        showNotification('No hay datos para exportar.', 'error');
        return;
      }

      // ── 4. Generar workbook y ajustar anchos de columna ──
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Resultados');

      // Ancho de columnas: mínimo 15, máximo 50, basado en nombre del header
      const colKeys = Object.keys(rows[0]);
      ws['!cols'] = colKeys.map(key => ({ wch: Math.min(50, Math.max(key.length + 2, 15)) }));

      // ── 5. Descargar ──
      const safeName = (summary.templateName || 'ejecucion').replace(/[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ_ -]/g, '_');
      const filename = `Ejecucion_${summary.id}_${safeName}.xlsx`;
      XLSX.writeFile(wb, filename);

      showNotification('Reporte Excel descargado exitosamente', 'success');
    } catch (error) {
      logger.error('Error al exportar Excel:', { error });
      showNotification('Error al generar el reporte Excel', 'error');
    } finally {
      setIsExcelLoading(false);
    }
  };

  // Reiniciar proceso
  const resetProcess = () => {
    setSelectedTemplate(null);
    setUploadedFile(null);
    setTusUploadId(null);
    setUploadProgress(0);
    setIsUploading(false);
    setExecutionResults(null);
    setCurrentStep('template');
    setShowReport(false);
    setValidationComments({});
    setTemplateValidationCounts(null);
    if (tusUploadRef.current) { tusUploadRef.current.abort(); tusUploadRef.current = null; }
  };

  if (!canExecute) {
    return (
      <BaseCard className="p-6">
        <div className="text-center text-red-600">
          <AlertCircle className="w-16 h-16 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Acceso Denegado</h3>
          <p>No tienes permisos para acceder al módulo de ejecución de validaciones.</p>
        </div>
      </BaseCard>
    );
  }

  // Agrupar resultados por tipo (fuera del JSX para mejor legibilidad)
  const groupedResults = executionResults?.results?.reduce((acc, result) => {
    const type = result.type || 'unknown';
    if (!acc[type]) acc[type] = [];
    acc[type].push(result);
    return acc;
  }, {});

  const typeOrder = ['structure', 'syntactic', 'domain', 'relational', 'topological'];
  const typeLabels = {
    structure: 'Estructural',
    syntactic: 'Sintáctica',
    domain: 'Dominio',
    relational: 'Relacional',
    topological: 'Topológica'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de ejecuciones</h1>
          <p className="text-gray-600">Consulte los estados de las validaciones en ejecución o inicie una nueva</p>
        </div>
        {currentStep === 'template' && (
          <BaseButton 
            onClick={handleTemplateNew}
            className="flex items-center gap-2"
          >
            <Play size={20} />
            Nueva ejecución
          </BaseButton>
        )}
      </div>

      {/* Indicador de pasos */}
      {/* <div className="flex items-center justify-center space-x-4 mb-6">
        {[
          { step: 'template', label: 'Buscar plantilla', icon: Database },
          { step: 'file', label: 'Cargar Archivo', icon: Upload },
          { step: 'execute', label: 'Ejecutar', icon: Play },
          { step: 'results', label: 'Resultados', icon: BarChart3 }
        ].map(({ step, label, icon: Icon }, index) => (
          <div key={step} className="flex items-center">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
              currentStep === step ? 'bg-blue-500 text-white' : 
              ['template', 'file', 'execute'].indexOf(currentStep) > ['template', 'file', 'execute'].indexOf(step) || 
              currentStep === 'results' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              <Icon className="w-4 h-4" />
            </div>
            <span className={`ml-2 text-sm ${
              currentStep === step ? 'font-semibold text-blue-600' : 'text-gray-600'
            }`}>
              {label}
            </span>
            {index < 3 && <div className="w-8 h-px bg-gray-300 mx-4" />}
          </div>
        ))}
      </div> */}

      {/* Paso 1: Seleccionar Plantilla */}
      {currentStep === 'template' && (
        <BaseCard>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4 items-end">
                <div className="md:col-span-1">
                    <label htmlFor="search-template" className="text-sm text-gray-600 block mb-1">Plantilla</label>
                    <input
                        id="search-template"
                        type="text"
                        value={templateSearch}
                        onChange={e => setTemplateSearch(e.target.value)}
                        placeholder="Nombre o descripción..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label htmlFor="status-filter" className="text-sm text-gray-600 block mb-1">Estado</label>
                    <select
                        id="status-filter"
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">Todos los estados</option>
                        {TEMPLATE_STATUSES.map(status => (
                            <option key={status.label} value={status.label}>{status.label}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label htmlFor="start-date" className="text-sm text-gray-600 block mb-1">Fecha de inicio</label>
                    <input
                        id="start-date"
                        type="date"
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label htmlFor="end-date" className="text-sm text-gray-600 block mb-1">Fecha de fin</label>
                    <input
                        id="end-date"
                        type="date"
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <BaseButton
                        variant="outline"
                        onClick={clearFilters}
                        className="w-full flex items-center justify-center gap-2"
                    >
                        <X size={16} /> Limpiar
                    </BaseButton>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {executionsLoading && <p className="text-sm text-gray-500 col-span-3 text-center py-4">Cargando ejecuciones...</p>}
              {executions
                .slice()
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .filter(t => {
                  const searchMatch = t.name.toLowerCase().includes(templateSearch.toLowerCase()) ||
                                    t.description.toLowerCase().includes(templateSearch.toLowerCase());
                  const startDateMatch = !startDate || new Date(t.createdAt) >= new Date(startDate);
                  const endDateMatch = !endDate || new Date(t.createdAt) <= new Date(endDate);
                  const statusMatch = statusFilter === 'all' || t.status.label === statusFilter;
                  return searchMatch && startDateMatch && endDateMatch && statusMatch;
                })
                .map(template => (
                <div
                  key={template.id}
                  className={`border rounded-lg p-4 transition-all ${
                    !['En cola', 'Procesando'].includes(template.status.label)
                      ? 'cursor-pointer hover:border-gray-300'
                      : 'cursor-default'
                  } ${
                    selectedTemplate?.id === template.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                  onClick={() => {
                    if (['Completado', 'Completado con advertencias', 'Completado con errores'].includes(template.status.label)) {
                      if (template._raw) {
                        handleViewRealExecution(template);
                      } else {
                        handleViewResults(template);
                      }
                    } else if (template.status.label === 'Error') {
                      showErrorModal(template);
                    } // No hacer nada para 'En cola' o 'Procesando'
                  }}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-semibold text-gray-900">{template.name}</h3>
                    <div className="flex items-center gap-1">
                      {/* Indicador de origen: solo visible para ejecuciones del Add-in */}
                      {template.sourceType && template.sourceType !== 'web_upload' && (
                        <span
                          title={template.sourceType === 'addin_sde' ? 'Validación desde Add-in (SDE)' : 'Validación desde Add-in (GDB local)'}
                          className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700 border border-indigo-200"
                        >
                          Add-in
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${template.status.color}`}>{template.status.label}</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>{template.validationCount} validaciones</span>
                    <span>
                      {/* Fecha de inicio */}
                      <span className="flex items-center">
                        <span>Inicio:&nbsp;</span>
                        <span className="text-right ml-auto min-w-[110px] block">
                          {new Date(template.createdAt).toLocaleString('es-CO', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </span>
                      </span>
                      {/* Fecha de terminado y tiempo transcurrido solo para Completado/Error */}
                      {['Completado', 'Completado con advertencias', 'Completado con errores', 'Error'].includes(template.status.label) && (() => {
                        const inicio = new Date(template.createdAt);
                        const terminado = template.terminadoAt
                          ? new Date(template.terminadoAt)
                          : new Date();
                        const diffMs = terminado - inicio;
                        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                        const diffHrs = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                        const diffMin = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                        return (
                          <>
                            <span className="flex items-center">
                              <span>Fin:&nbsp;</span>
                              <span className="text-right ml-auto min-w-[110px] block">
                                {terminado.toLocaleString('es-CO', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true
                                })}
                              </span>
                            </span>
                            <span className="flex items-center">
                              <span>Tiempo:&nbsp;</span>
                              <span className="text-blue-600 font-semibold text-right ml-auto min-w-[70px] block">
                                {diffDays > 0 ? `${diffDays}d ` : ''}{diffHrs}h {diffMin}min
                              </span>
                            </span>
                          </>
                        );
                      })()}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-gray-400">
                    FC: {template.structure?.featureClasses?.length || 0} |
                    Tablas: {template.structure?.tables?.length || 0} |
                    Dominios: {template.structure?.domains?.length || 0}
                  </div>
                </div>
              ))}
              {executions
                .filter(t => {
                    const searchMatch = t.name.toLowerCase().includes(templateSearch.toLowerCase()) || t.description.toLowerCase().includes(templateSearch.toLowerCase());
                    const startDateMatch = !startDate || new Date(t.createdAt) >= new Date(startDate);
                    const endDateMatch = !endDate || new Date(t.createdAt) <= new Date(endDate);
                    const statusMatch = statusFilter === 'all' || t.status.label === statusFilter;
                    return searchMatch && startDateMatch && endDateMatch && statusMatch;
                }).length === 0 && (
                <div className="col-span-full text-center text-gray-400 py-8">
                  <AlertCircle className="mx-auto mb-2" size={32} />
                  No se encontraron plantillas.
                </div>
              )}
            </div>
          </div>
        </BaseCard>
      )}

      {/* Paso 2: Cargar Archivo */}
      {currentStep === 'new' && (
        <BaseCard>
          <div className="p-6 space-y-8">
            {/* Paso 1: Selección de Plantilla */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">1. Seleccionar Plantilla</h2>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar plantilla por nombre o descripción..."
                  value={selectedTemplate ? newExecutionTemplateSearch : newExecutionTemplateSearch}
                  onChange={e => {
                    setNewExecutionTemplateSearch(e.target.value);
                    if (selectedTemplate) setSelectedTemplate(null);
                  }}
                  onFocus={() => setShowTemplateOptions(true)}
                  onBlur={() => setTimeout(() => setShowTemplateOptions(false), 150)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {showTemplateOptions && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {availableTemplates.filter(t =>
                      (t.status === 'active' || t.isActive === true) &&
                      (t.name.toLowerCase().includes(newExecutionTemplateSearch.toLowerCase()) ||
                      (t.description || '').toLowerCase().includes(newExecutionTemplateSearch.toLowerCase()))
                    ).map(t => (
                      <div
                        key={t.id}
                        onMouseDown={() => {
                          setSelectedTemplate(t);
                          setNewExecutionTemplateSearch(t.name);
                          setShowTemplateOptions(false);
                          loadTemplateValidationCounts(t.id);
                        }}
                        className="p-3 cursor-pointer hover:bg-blue-50"
                      >
                        <h4 className="font-medium text-gray-900 text-sm">{t.name}</h4>
                        <p className="text-xs text-gray-600">{t.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {selectedTemplate && (
                <div className="border-t pt-4">
                  <h3 className="text-md font-semibold mb-3">Validaciones en <span className="text-blue-600">{selectedTemplate.name}</span></h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {['structure', 'syntactic', 'domain', 'relational', 'topological'].map(type => {
                      const count = templateValidationCounts ? templateValidationCounts[type] : 0;
                      const typeLabels = {
                        structure:   'Estructurales',
                        topological: 'Topológicas',
                        syntactic:   'Sintácticas',
                        domain:      'Dominio',
                        relational:  'Relacional'
                      };
                      return (
                        <BaseCard key={type} className="p-3 text-center">
                          <p className="text-xl font-bold text-gray-800">{count}</p>
                          <p className="text-xs font-medium text-gray-600">{typeLabels[type]}</p>
                        </BaseCard>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Paso 2: Carga de Archivo (visible solo si se ha seleccionado una plantilla) */}
            {selectedTemplate && (
              <div className="space-y-4 border-t pt-8">
                <h2 className="text-lg font-semibold">2. Cargar Archivo GeoDatabase</h2>
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <Upload className="mx-auto text-gray-400 mb-4" size={48} />
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      Arrastre y suelte su archivo .zip aquí, o
                    </p>
                    <label className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors ${isUploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'} text-white`}>
                      <Upload className="mr-2" size={16} />
                      {isUploading ? 'Subiendo...' : 'Seleccionar archivo'}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".zip"
                        onChange={handleFileUpload}
                        disabled={isUploading}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {/* Barra de progreso del upload */}
                  {uploadedFile && (
                    <div className="mt-4">
                      {isUploading ? (
                        <div>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-blue-700 font-medium flex items-center gap-1">
                              <Loader2 size={14} className="animate-spin" />
                              Subiendo {uploadedFile.name}
                            </span>
                            <span className="text-blue-700 font-bold">{uploadProgress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div
                              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                              style={{ width: `${uploadProgress}%` }}
                            />
                          </div>
                          <p className="text-xs text-warning-text font-medium mt-2">
                            ⚠ No cierre ni recargue esta ventana durante la subida
                          </p>
                        </div>
                      ) : tusUploadId ? (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center justify-center text-green-800">
                            <CheckCircle className="mr-2" size={16} />
                            <span className="text-sm font-medium">{uploadedFile.name} — Listo para ejecutar</span>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end items-center gap-4 mt-6 pt-6 border-t">
              <BaseButton
                variant="outline"
                onClick={resetProcess}
              >
                Cancelar
              </BaseButton>
              <BaseButton
                onClick={executeValidations}
                disabled={isExecuting || isUploading || !selectedTemplate || !tusUploadId}
                className="flex items-center gap-2"
              >
                {isExecuting ? (
                  <>
                    <Clock className="w-4 h-4 animate-spin" />
                    Ejecutando...
                  </>
                ) : isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Subiendo archivo...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Ejecutar
                  </>
                )}
              </BaseButton>
            </div>
          </div>
        </BaseCard>
      )}

      {/* Paso 2: Cargar Archivo */}
      {currentStep === 'file' && (
        <BaseCard>
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Cargar Archivo GeoDatabase
            </h2>
            
            {/* Información de plantilla seleccionada */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-2">Plantilla seleccionada: {selectedTemplate.name}</h3>
              <p className="text-blue-700 text-sm mb-2">{selectedTemplate.description}</p>
              <div className="text-sm text-blue-600">
                Validaciones activas: {templateValidationCounts ?
                  templateValidationCounts.structure + templateValidationCounts.syntactic +
                  templateValidationCounts.domain + templateValidationCounts.relational +
                  templateValidationCounts.topological : 0}
              </div>
            </div>

            {/* Área de carga */}
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload className="mx-auto text-gray-400 mb-4" size={48} />
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  Arrastre y suelte su archivo .zip aquí, o
                </p>
                <label className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors ${isUploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'} text-white`}>
                  <Upload className="mr-2" size={16} />
                  {isUploading ? 'Subiendo...' : 'Seleccionar archivo'}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".zip"
                    onChange={handleFileUpload}
                    disabled={isUploading}
                    className="hidden"
                  />
                </label>
              </div>

              {/* Barra de progreso del upload */}
              {uploadedFile && (
                <div className="mt-4">
                  {isUploading ? (
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-blue-700 font-medium flex items-center gap-1">
                          <Loader2 size={14} className="animate-spin" />
                          Subiendo {uploadedFile.name}
                        </span>
                        <span className="text-blue-700 font-bold">{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <p className="text-xs text-warning-text font-medium mt-2">
                        ⚠ No cierre ni recargue esta ventana durante la subida
                      </p>
                    </div>
                  ) : tusUploadId ? (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-center text-green-800">
                        <CheckCircle className="mr-2" size={16} />
                        <span className="text-sm font-medium">{uploadedFile.name}</span>
                      </div>
                      <div className="text-sm text-green-600 mt-1">
                        Tamaño: {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB — Listo para ejecutar
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* Botón continuar — deshabilitado hasta que el upload esté completo */}
            <div className="mt-6 text-center">
              <BaseButton
                onClick={() => setCurrentStep('execute')}
                className="flex items-center gap-2"
                disabled={!tusUploadId || isUploading}
              >
                <Play className="w-4 h-4" />
                {isUploading ? 'Esperando subida...' : 'Continuar a Ejecución'}
              </BaseButton>
            </div>
          </div>
        </BaseCard>
      )}

      {/* Paso 3: Ejecutar Validaciones */}
      {currentStep === 'execute' && (
        <BaseCard>
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Play className="w-5 h-5" />
              Ejecutar Validaciones
            </h2>

            {/* Resumen de ejecución */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-700">Plantilla</h3>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="font-medium">{selectedTemplate.name}</div>
                  <div className="text-sm text-gray-600">{selectedTemplate.description}</div>
                </div>
              </div>
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-700">Archivo</h3>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="font-medium">{uploadedFile.name}</div>
                  <div className="text-sm text-gray-600">
                    {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>
              </div>
            </div>

            {/* Validaciones a ejecutar */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-700 mb-3">
                Validaciones a ejecutar ({MOCK_VALIDATIONS[selectedTemplate.id]?.filter(v => v.isActive).length || 0})
              </h3>
              <div className="space-y-2">
                {MOCK_VALIDATIONS[selectedTemplate.id]?.filter(v => v.isActive).map(validation => (
                  <div key={validation.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className={`w-3 h-3 rounded-full ${
                      validation.severity === 'error' ? 'bg-red-500' : 'bg-yellow-500'
                    }`} />
                    <div className="flex-1">
                      <div className="font-medium">{validation.name}</div>
                      <div className="text-sm text-gray-600">{validation.description}</div>
                    </div>
                    <div className="text-xs text-gray-500 capitalize">
                      {validation.type}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Botones de acción */}
            <div className="flex justify-center items-center gap-4">
              <BaseButton
                variant="outline"
                onClick={() => setCurrentStep('file')}
              >
                ← Volver
              </BaseButton>
              <BaseButton
                onClick={executeValidations}
                disabled={isExecuting}
                className="flex items-center gap-2 px-8 py-3"
              >
                {isExecuting ? (
                  <>
                    <Clock className="w-4 h-4 animate-spin" />
                    Ejecutando Validaciones...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Ejecutar Validaciones
                  </>
                )}
              </BaseButton>
            </div>

            {/* Progreso de ejecución */}
            {isExecuting && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2 text-blue-700 mb-2">
                  <Clock className="w-4 h-4 animate-spin" />
                  <span className="font-medium">Ejecutando validaciones...</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full animate-pulse" style={{width: '60%'}}></div>
                </div>
                <div className="text-sm text-blue-600 mt-2">
                  Procesando archivo GeoDatabase y aplicando reglas de validación...
                </div>
              </div>
            )}
          </div>
        </BaseCard>
      )}

      {/* Paso 4: Resultados */}
      {currentStep === 'results' && executionResults && (
        <div className="space-y-6">
          {/* Resumen de resultados */}
          <BaseCard>
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Resultados de Validación
                </h2>
                <div className="flex gap-2">
                  <BaseButton
                    onClick={handleBackToTemplateList}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Regresar
                  </BaseButton>
                  <BaseButton
                    onClick={generateReport}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    Ver Reporte
                  </BaseButton>
                  <BaseButton
                    onClick={exportResults}
                    variant="outline"
                    className="flex items-center gap-2"
                    disabled={isPdfLoading}
                  >
                    {isPdfLoading
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Download className="w-4 h-4" />}
                    {isPdfLoading ? 'Generando...' : 'PDF'}
                  </BaseButton>
                  <BaseButton
                    onClick={exportToExcel}
                    variant="outline"
                    className="flex items-center gap-2"
                    disabled={isExcelLoading}
                  >
                    {isExcelLoading
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Download className="w-4 h-4" />}
                    {isExcelLoading ? 'Generando...' : 'Excel'}
                  </BaseButton>
                </div>
              </div>

              {/* Información de ejecución */}
              <div className="bg-white border-l-4 border-primary p-4 rounded-r-lg mb-6 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-2">
                  <div className="text-gray-700">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Plantilla</p>
                    <p className="text-md font-bold">{executionResults.templateName}</p>
                  </div>
                  <div className="text-gray-700">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Archivo</p>
                    <p className="text-md font-bold">{executionResults.fileName}</p>
                  </div>
                  <div className="text-gray-700">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Ejecutado</p>
                    <p className="text-md font-bold">{new Date(executionResults.executedAt).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Métricas generales */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="text-2xl font-bold text-blue-600">
                    {executionResults.totalValidations}
                  </div>
                  <div className="text-sm text-blue-700">Total Validaciones</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="text-2xl font-bold text-green-600">
                    {executionResults.passedValidations}
                  </div>
                  <div className="text-sm text-green-700">Superadas</div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <div className="text-2xl font-bold text-red-600">
                    {executionResults.failedErrorValidations}
                  </div>
                  <div className="text-sm text-red-700">No Superadas (Error)</div>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <div className="text-2xl font-bold text-yellow-600">
                    {executionResults.failedWarningValidations}
                  </div>
                  <div className="text-sm text-yellow-700">No Superadas (Warn)</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-gray-600">
                    {executionResults.failedInfoValidations}
                  </div>
                  <div className="text-sm text-gray-700">No Superadas (Info)</div>
                </div>
              </div>
            </div>

            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Detalle de Resultados</h3>

              {/* Renderizar grupos por tipo */}
              {typeOrder.map(type => {
                const results = groupedResults?.[type] || [];
                if (results.length === 0) return null;

                const isExpanded = expandedGroups[type];

                // Health Check: calcular métricas para color del encabezado
                const failedResults = results.filter(r => !(r.status === 'ok' || r.status === 'passed' || r.hallazgos?.length === 0));
                const hasFailedError = failedResults.some(r => r.severity === 'error');
                const hasFailedWarning = failedResults.some(r => r.severity === 'warning');
                const hasFailedInfo = failedResults.some(r => r.severity === 'info');

                const headerColor = hasFailedError ? 'red' : hasFailedWarning ? 'orange' : hasFailedInfo ? 'blue' : 'green';
                const headerBg = hasFailedError ? 'from-red-50 to-error-light' : hasFailedWarning ? 'from-warning-light to-yellow-50' : hasFailedInfo ? 'from-blue-50 to-indigo-50' : 'from-green-50 to-emerald-50';
                const headerBorder = hasFailedError ? 'border-red-300' : hasFailedWarning ? 'border-warning' : hasFailedInfo ? 'border-blue-200' : 'border-green-300';

                return (
                  <div key={type} className="mb-6">
                    {/* Cabecera del grupo */}
                    <div
                      className={`flex items-center justify-between p-3 bg-gradient-to-r ${headerBg} border ${headerBorder} rounded-lg cursor-pointer hover:opacity-90 transition-all shadow-sm hover:shadow`}
                      onClick={() => toggleGroup(type)}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`transform transition-transform duration-200 ${headerColor === 'red' ? 'text-red-600' : headerColor === 'green' ? 'text-green-600' : headerColor === 'orange' ? 'text-warning-text' : 'text-blue-600'} ${isExpanded ? 'rotate-90' : ''}`}>
                          ▶
                        </span>
                        <h4 className={`font-semibold ${headerColor === 'red' ? 'text-red-900' : headerColor === 'green' ? 'text-green-900' : headerColor === 'orange' ? 'text-warning-text' : 'text-blue-900'}`}>
                          {typeLabels[type] || type}
                        </h4>
                        <span className="px-2 py-1 bg-white rounded-full text-xs font-medium text-gray-600 shadow-sm">
                          {results.length}
                        </span>
                        {hasFailedError && (
                          <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20" title="Contiene errores críticos">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className={`text-sm font-medium transition-colors ${isExpanded ? (headerColor === 'red' ? 'text-red-700' : headerColor === 'green' ? 'text-green-700' : headerColor === 'orange' ? 'text-warning-text' : 'text-blue-700') : 'text-gray-500'}`}>
                        {isExpanded ? 'Ocultar' : 'Mostrar'}
                      </span>
                    </div>

                    {/* Filtros de resultados */}
                    <div className="flex flex-wrap gap-2 mt-3 px-3">
                      {/* Inicializar filtros si no existen */}
                      {!activeFilters[type] && setActiveFilters(prev => ({
                        ...prev,
                        [type]: { passed: true, failed: true, error: true, warning: true, info: true }
                      }))}

                      {(() => {
                        const filters = activeFilters[type] || { passed: true, failed: true, error: true, warning: true, info: true };
                        const passedCount = results.filter(r => r.status === 'ok' || r.status === 'passed' || r.hallazgos?.length === 0).length;
                        const failedCount = results.filter(r => !(r.status === 'ok' || r.status === 'passed' || r.hallazgos?.length === 0)).length;
                        const errorCount = results.filter(r => r.severity === 'error').length;
                        const warningCount = results.filter(r => r.severity === 'warning').length;
                        const infoCount = results.filter(r => r.severity === 'info').length;

                        const toggleFilter = (key) => {
                          // Determinar a qué grupo pertenece el filtro
                          const estadoGroup = ['passed', 'failed'];
                          const severidadGroup = ['error', 'warning', 'info'];
                          
                          // Contar filtros activos en el grupo
                          const activeInGroup = estadoGroup.includes(key)
                            ? estadoGroup.filter(k => filters[k]).length
                            : severidadGroup.filter(k => filters[k]).length;
                          
                          // Si es el único activo en su grupo, no permitir desactivar
                          if (activeInGroup === 1 && filters[key]) {
                            return; // Ignorar click
                          }
                          
                          setActiveFilters(prev => ({
                            ...prev,
                            [type]: { ...filters, [key]: !filters[key] }
                          }));
                        };

                        return (
                          <>
                            <button
                              onClick={() => toggleFilter('passed')}
                              className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer ${
                                filters.passed
                                  ? 'bg-green-100 text-green-700 ring-2 ring-green-400'
                                  : 'bg-gray-100 text-gray-400 opacity-50'
                              }`}
                              title={!filters.passed && filters.failed ? 'No se puede desactivar (último filtro de Estado)' : ''}
                            >
                              ✓ Superadas: {passedCount}
                            </button>
                            <button
                              onClick={() => toggleFilter('failed')}
                              className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer ${
                                filters.failed
                                  ? 'bg-red-100 text-red-700 ring-2 ring-red-400'
                                  : 'bg-gray-100 text-gray-400 opacity-50'
                              }`}
                              title={!filters.failed && filters.passed ? 'No se puede desactivar (último filtro de Estado)' : ''}
                            >
                              ⚠ No Superadas: {failedCount}
                            </button>
                            <div className="w-px h-6 bg-gray-300 self-center"></div>
                            <button
                              onClick={() => toggleFilter('error')}
                              className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer ${
                                filters.error
                                  ? 'bg-red-50 text-red-600 ring-2 ring-red-300'
                                  : 'bg-gray-100 text-gray-400 opacity-50'
                              }`}
                              title={!filters.error && (filters.warning || filters.info) ? 'No se puede desactivar (último filtro de Severidad)' : ''}
                            >
                              ♦ Error: {errorCount}
                            </button>
                            <button
                              onClick={() => toggleFilter('warning')}
                              className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer ${
                                filters.warning
                                  ? 'bg-warning-light text-warning-text ring-2 ring-warning'
                                  : 'bg-gray-100 text-gray-400 opacity-50'
                              }`}
                              title={!filters.warning && (filters.error || filters.info) ? 'No se puede desactivar (último filtro de Severidad)' : ''}
                            >
                              ♦ Advertencia: {warningCount}
                            </button>
                            <button
                              onClick={() => toggleFilter('info')}
                              className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer ${
                                filters.info
                                  ? 'bg-blue-50 text-blue-600 ring-2 ring-blue-300'
                                  : 'bg-gray-100 text-gray-400 opacity-50'
                              }`}
                              title={!filters.info && (filters.error || filters.warning) ? 'No se puede desactivar (último filtro de Severidad)' : ''}
                            >
                              ♦ Información: {infoCount}
                            </button>
                          </>
                        );
                      })()}
                    </div>

                    {/* Lista de resultados del grupo (filtrados) */}
                    {isExpanded && (
                      <div className="mt-3 space-y-3">
                        {(() => {
                          const filters = activeFilters[type] || { passed: true, failed: true, error: true, warning: true, info: true };
                          
                          // Filtrar resultados según filtros activos
                          const filteredResults = results.filter(result => {
                            const isPassed = result.isPassed ?? (result.status === 'ok' || result.status === 'passed' || result.hallazgos?.length === 0);
                            const isFailed = !isPassed;
                            
                            // Verificar filtro de estado
                            const stateMatch = (isPassed && filters.passed) || (isFailed && filters.failed);
                            
                            // Verificar filtro de severidad
                            const severityMatch = 
                              (result.severity === 'error' && filters.error) ||
                              (result.severity === 'warning' && filters.warning) ||
                              (result.severity === 'info' && filters.info);
                            
                            return stateMatch && severityMatch;
                          });

                          if (filteredResults.length === 0) {
                            return (
                              <div className="text-center py-8 text-gray-500 text-sm">
                                No hay resultados que mostrar con los filtros actuales
                              </div>
                            );
                          }

                          return filteredResults.map(result => (
                          <div
                            key={result.id}
                            className="border border-gray-200 rounded-lg p-4 cursor-pointer transition-all hover:bg-gray-50"
                            onClick={() => {
                              const newId = expandedValidationId === result.id ? null : result.id;
                              setExpandedValidationId(newId);
                              if (newId) {
                                api.get(`/api/validation-comments/result/${newId}`)
                                  .then(comments => {
                                    if (comments?.length > 0) {
                                      setValidationComments(prev => ({
                                        ...prev,
                                        [newId]: {
                                          content: comments[0].content,
                                          createdBy: comments[0].createdBy,
                                          createdAt: comments[0].createdAt
                                        }
                                      }));
                                    }
                                  });
                              }
                            }}>

                            <div className="flex items-start gap-3">
                              <div className="flex-1">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="font-semibold">{result.name}</h4>
                                    {/* Badge de Estado - basado en si hay hallazgos (fallido) o no (exitoso) */}
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${
                                      result.status === 'ok' || result.status === 'passed' || result.hallazgos?.length === 0
                                        ? 'bg-green-100 text-green-700 border-2 border-green-300'
                                        : 'bg-red-100 text-red-700 border-2 border-red-300'
                                    }`}>
                                      {result.status === 'ok' || result.status === 'passed' || result.hallazgos?.length === 0 ? (
                                        <>
                                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                          </svg>
                                          SUPERADA
                                        </>
                                      ) : (
                                        <>
                                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                          </svg>
                                          NO SUPERADA
                                        </>
                                      )}
                                    </span>

                                    {/* Separador y Label del Operador */}
                                    {result.rule?.operator && (
                                      <>
                                        <span className="text-gray-300 mx-2">|</span>
                                        <span
                                          className="text-gray-400 text-xs italic truncate max-w-[200px]"
                                          title={operatorsByType[result.type]?.find(op => op.value === result.rule.operator)?.label || result.rule.operator}
                                        >
                                          {operatorsByType[result.type]?.find(op => op.value === result.rule.operator)?.label || result.rule.operator}
                                        </span>
                                      </>
                                    )}
                                  </div>

                                  {/* Severidad de la Regla - esquina superior derecha */}
                                  <div className="flex items-center gap-1 text-xs text-gray-500" title="Severidad de la regla">
                                    <span className="font-medium">Severidad:</span>
                                    <span className={`font-bold text-base mx-1 ${
                                      result.severity === 'error' ? 'text-red-600' :
                                      result.severity === 'warning' ? 'text-yellow-600' :
                                      result.severity === 'info' ? 'text-blue-600' :
                                      'text-gray-600'
                                    }`}>
                                      ♦
                                    </span>
                                    <span className="font-medium capitalize">
                                      {result.severity === 'error' ? 'Error' :
                                       result.severity === 'warning' ? 'Advertencia' :
                                       result.severity === 'info' ? 'Informativa' :
                                       result.severity}
                                    </span>
                                  </div>
                                </div>
                                <p className="text-sm text-gray-600 mb-3">{result.message}</p>
                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                  <span>Total registros: {result.hallazgos?.length || 0}</span>
                                </div>
                              </div>
                            </div>

                            {expandedValidationId === result.id && (
                              <div className="mt-4 p-3 bg-white rounded border" onClick={(e) => e.stopPropagation()}>
                                <h4 className="font-semibold text-gray-700 mb-2">Hallazgos de la validación</h4>

                                {/* Tabla de hallazgos simplificada - Solo si hay hallazgos */}
                                {result.hallazgos && result.hallazgos.length > 0 ? (
                                  <div className="overflow-x-auto mb-4">
                                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                                      <thead className="bg-gray-50">
                                        <tr>
                                          <th
                                            className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-20"
                                            onClick={handleHallazgoSort}
                                            title="Ordenar por ID"
                                          >
                                            # {hallazgoSortDirection === 'asc' ? '↑' : '↓'}
                                          </th>
                                          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Validación</th>
                                          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                                          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Capa</th>
                                          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Operador</th>
                                          <th className="px-3 py-2 text-center font-medium text-gray-500 uppercase tracking-wider w-16"></th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-gray-200">
                                        {sortHallazgos(result.hallazgos).map((example, index) => (
                                          <tr key={index} className="hover:bg-gray-50">
                                            <td className="px-3 py-2 whitespace-nowrap text-gray-900 font-mono">
                                              {example.id !== null && example.id !== undefined && example.id !== -1 ? example.id : '-'}
                                            </td>
                                            <td className="px-3 py-2">
                                              <div className="text-gray-900 truncate max-w-xs" title={result.name}>
                                                {result.name}
                                              </div>
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                              <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full capitalize bg-gray-100 text-gray-700">
                                                {validationTypes.find(t => t.value === result.type)?.label || result.type}
                                              </span>
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                                              {example.feature || result.layerName || '-'}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-gray-900 font-mono text-xs">
                                              {result.rule.operator}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-center">
                                              <button
                                                onClick={() => handleShowValidationDetails(result, example)}
                                                className="text-gray-400 hover:text-blue-600 transition-colors"
                                                title="Ver detalle"
                                              >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  /* Mensaje cuando no hay hallazgos */
                                  <div className="mb-4 p-4 bg-green-50 rounded-lg text-center border border-green-200">
                                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                                    <p className="font-medium text-green-800">
                                      {result.status === 'ok' || result.status === 'passed' 
                                        ? '¡Validación exitosa!' 
                                        : 'No se encontraron hallazgos específicos'}
                                    </p>
                                    <p className="text-xs text-green-600 mt-1">
                                      {result.hallazgos?.length === 0 
                                        ? 'No hay registros que mostrar en esta validación' 
                                        : 'Esta validación no generó hallazgos individuales'}
                                    </p>
                                  </div>
                                )}

                                <div className="mt-4">
                                  <label className="block text-sm font-medium text-gray-700 mb-2">Comentario</label>
                                  <textarea
                                    rows={3}
                                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                    placeholder="Añadir comentario para esta validación..."
                                    value={validationComments[result.id]?.content ?? validationComments[result.id] ?? ''}
                                    onChange={(e) => setValidationComments(prev => ({
                                      ...prev,
                                      [result.id]: { ...(prev[result.id] || {}), content: e.target.value }
                                    }))}
                                  />
                                </div>

                                <div className="flex justify-end mt-3">
                                  <BaseButton
                                    onClick={() => handleSaveComments(result.id)}
                                  >
                                    Guardar comentario
                                  </BaseButton>
                                </div>
                              </div>
                            )}
                          </div>
                        ));
                      })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </BaseCard>
        </div>
      )}

      {/* Modal de Error */}
      {errorModalContent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Error en la Ejecución</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-700">{errorModalContent}</p>
            </div>
            <div className="flex justify-end p-4 bg-gray-50 border-t">
              <BaseButton
                onClick={() => setErrorModalContent(null)}
                variant="outline"
              >
                Cerrar
              </BaseButton>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Reporte */}
      {showReport && executionResults && (() => {
        const hasErrors   = (executionResults.failedErrorValidations ?? 0) > 0;
        const hasWarnings = (executionResults.failedWarningValidations ?? 0) > 0;
        const veredicto   = hasErrors
          ? { label: 'RECHAZADO',                  cls: 'bg-red-100 text-red-800 border border-red-300' }
          : hasWarnings
          ? { label: 'APROBADO CON ADVERTENCIAS',  cls: 'bg-yellow-100 text-yellow-800 border border-yellow-300' }
          : { label: 'APROBADO',                   cls: 'bg-green-100 text-green-800 border border-green-300' };

        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-[95vw] max-w-7xl max-h-[90vh] overflow-y-auto">

            {/* Cabecera sticky */}
            <div className="sticky top-0 bg-white border-b p-6 z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Reporte de Validación</h2>
                <BaseButton onClick={() => setShowReport(false)} variant="outline" size="sm">Cerrar</BaseButton>
              </div>
            </div>

            <div className="p-6 space-y-6">

              {/* Encabezado */}
              <div className="text-center border-b pb-6">
                <h1 className="text-2xl font-bold mb-3">Reporte de Validación de GeoDatabase</h1>
                <span className={`inline-block px-4 py-2 rounded-full text-sm font-bold mb-4 ${veredicto.cls}`}>
                  {veredicto.label}
                </span>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-gray-600 mt-2">
                  <div className="bg-gray-50 rounded p-2"><p className="text-xs text-gray-400 mb-1">Plantilla</p><p className="font-semibold text-gray-800">{executionResults.templateName}</p></div>
                  <div className="bg-gray-50 rounded p-2"><p className="text-xs text-gray-400 mb-1">Archivo GDB</p><p className="font-semibold text-gray-800 truncate">{executionResults.fileName}</p></div>
                  <div className="bg-gray-50 rounded p-2"><p className="text-xs text-gray-400 mb-1">Fecha de ejecución</p><p className="font-semibold text-gray-800">{new Date(executionResults.executedAt).toLocaleString('es-CO')}</p></div>
                  <div className="bg-gray-50 rounded p-2"><p className="text-xs text-gray-400 mb-1">Usuario</p><p className="font-semibold text-gray-800">{user?.username || user?.name || ''}</p></div>
                </div>
              </div>

              {/* Resumen ejecutivo — grid de métricas */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Resumen Ejecutivo</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-800">{executionResults.totalValidations}</p>
                    <p className="text-xs text-gray-500 mt-1">Total reglas</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-700">{executionResults.passedValidations}</p>
                    <p className="text-xs text-green-600 mt-1">Superadas</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-700">{executionResults.failedErrorValidations}</p>
                    <p className="text-xs text-red-600 mt-1">Errores</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-yellow-700">{executionResults.failedWarningValidations}</p>
                    <p className="text-xs text-yellow-600 mt-1">Advertencias</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-600">{executionResults.failedInfoValidations}</p>
                    <p className="text-xs text-gray-500 mt-1">Información</p>
                  </div>
                </div>
              </div>

              {/* Detalle de validaciones */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Detalle de Validaciones</h3>
                <div className="space-y-4">
                  {executionResults.results.map((result, index) => {
                    const isPassed = result.isPassed ?? (result.hallazgos?.length ?? 0) === 0;
                    const sevColor = result.severity === 'error'
                      ? 'bg-red-100 text-red-700'
                      : result.severity === 'warning'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-gray-100 text-gray-600';
                    const layerName = result.hallazgos?.[0]?.feature || null;
                    const fieldName = result.hallazgos?.[0]?.field || null;

                    return (
                      <div key={result.id} className="border rounded-lg overflow-hidden">
                        {/* Cabecera de la regla */}
                        <div className={`px-4 py-3 flex items-center justify-between ${isPassed ? 'bg-green-50' : 'bg-red-50'}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-semibold text-gray-500 shrink-0">#{index + 1}</span>
                            <h4 className="font-semibold text-gray-900 truncate">{result.name}</h4>
                          </div>
                          <span className={`shrink-0 ml-3 px-2 py-1 rounded-full text-xs font-bold ${isPassed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {isPassed ? 'SUPERADA' : 'NO SUPERADA'}
                          </span>
                        </div>

                        <div className="p-4 space-y-3">
                          {/* Tipo | Operador | Severidad */}
                          <div className="flex flex-wrap gap-2 text-xs">
                            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded font-medium">
                              Tipo: {result.type}
                            </span>
                            {result.rule?.operator && (
                              <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded font-medium">
                                Operador: {result.rule.operator}
                              </span>
                            )}
                            <span className={`px-2 py-1 rounded font-medium ${sevColor}`}>
                              Severidad: {result.severity}
                            </span>
                            {layerName && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded font-medium">
                                Capa: {layerName}
                              </span>
                            )}
                            {fieldName && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded font-medium">
                                Campo: {fieldName}
                              </span>
                            )}
                          </div>

                          {/* Mensaje */}
                          <div className="bg-gray-50 p-3 rounded text-sm text-gray-700">
                            {result.message}
                          </div>

                          {/* Comentario */}
                          {validationComments[result.id]?.content && (
                            <div className="rounded text-sm p-3" style={{ background: 'var(--warning-light)', borderLeft: '4px solid var(--secondary)' }}>
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-bold text-gray-700">
                                  {validationComments[result.id].createdBy || 'Analista'}
                                </p>
                                {validationComments[result.id].createdAt && (
                                  <p className="text-xs text-gray-500">
                                    {new Date(validationComments[result.id].createdAt).toLocaleString('es-CO')}
                                  </p>
                                )}
                              </div>
                              <p className="text-gray-800">{validationComments[result.id].content}</p>
                            </div>
                          )}

                          {/* Hallazgos */}
                          {result.hallazgos?.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                Hallazgos ({result.hallazgos.length})
                              </p>
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs border-collapse">
                                  <thead>
                                    <tr className="bg-gray-100">
                                      <th className="text-left px-3 py-2 font-semibold text-gray-600 border border-gray-200">Capa / Feature</th>
                                      <th className="text-left px-3 py-2 font-semibold text-gray-600 border border-gray-200">Campo</th>
                                      <th className="text-left px-3 py-2 font-semibold text-gray-600 border border-gray-200">ID Feature</th>
                                      <th className="text-left px-3 py-2 font-semibold text-gray-600 border border-gray-200">Detalle</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {result.hallazgos.map((h, hi) => {
                                      const decoded = h.fieldValue
                                        ? decodeHallazgoFieldValue(h.fieldValue, result.type)
                                        : null;
                                      return (
                                        <tr key={hi} className={hi % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                          <td className="px-3 py-2 border border-gray-200 text-gray-700">{h.feature || '—'}</td>
                                          <td className="px-3 py-2 border border-gray-200 text-gray-700">{h.field || '—'}</td>
                                          <td className="px-3 py-2 border border-gray-200 text-gray-700">{h.id ?? '—'}</td>
                                          <td className="px-3 py-2 border border-gray-200 text-gray-700">
                                            {decoded?.fields?.length > 0
                                              ? decoded.fields.map((f, fi) => (
                                                  <span key={fi} className="inline-block mr-3">
                                                    <span className="text-gray-400">{f.label}: </span>
                                                    <span className="font-medium">{String(f.value)}</span>
                                                  </span>
                                                ))
                                              : '—'}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {isPassed && (
                            <p className="text-xs text-green-600 font-medium">Sin hallazgos — regla superada correctamente.</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Conclusiones */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Conclusiones</h3>
                <div className="bg-blue-50 p-4 rounded-lg text-blue-800">
                  {!hasErrors && !hasWarnings ? (
                    <p><strong>Validación exitosa:</strong> Todas las validaciones fueron superadas correctamente. El archivo GeoDatabase cumple con todos los criterios establecidos en la plantilla.</p>
                  ) : (
                    <div>
                      <p className="mb-2">
                        <strong>Validación {hasErrors ? 'rechazada' : 'con observaciones'}:</strong> Se encontraron{' '}
                        <strong className="text-red-600">{executionResults.failedErrorValidations} errores</strong>,{' '}
                        <strong className="text-yellow-600">{executionResults.failedWarningValidations} advertencias</strong>{' '}
                        y <strong className="text-gray-600">{executionResults.failedInfoValidations} informaciones</strong>{' '}
                        de un total de {executionResults.totalValidations} reglas evaluadas.
                      </p>
                      <p>Se recomienda revisar y corregir los hallazgos identificados antes de proceder con el uso del archivo GeoDatabase en producción.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Pie */}
              <div className="border-t pt-4 text-center text-sm text-gray-500">
                <p>Reporte generado automáticamente por el Sistema de Validación de GeoDatabases — 10S</p>
                <p>Fecha de generación: {new Date().toLocaleString('es-CO')}</p>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Modal Ver Detalles del Hallazgo */}
      {showValidationDetailsModal && validationToShowDetails && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Detalles del Hallazgo
              </h3>
              <button
                onClick={() => {
                  setShowValidationDetailsModal(false);
                  setValidationToShowDetails(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              {/* 0. DESCRIPCIÓN DEL RESULTADO — mensaje humano de la validación */}
              {validationToShowDetails.message && (
                <div className={`rounded-lg px-4 py-3 flex items-start gap-3 ${
                  validationToShowDetails.status === 'ok' || validationToShowDetails.status === 'passed'
                    ? 'bg-green-50 border border-green-200'
                    : validationToShowDetails.severity === 'warning'
                    ? 'bg-yellow-50 border border-yellow-200'
                    : validationToShowDetails.severity === 'info'
                    ? 'bg-blue-50 border border-blue-200'
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <span className="mt-0.5 flex-shrink-0">
                    {validationToShowDetails.status === 'ok' || validationToShowDetails.status === 'passed' ? (
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                    ) : (
                      <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
                    )}
                  </span>
                  <p className={`text-sm font-medium ${
                    validationToShowDetails.status === 'ok' || validationToShowDetails.status === 'passed'
                      ? 'text-green-800'
                      : validationToShowDetails.severity === 'warning' ? 'text-yellow-800'
                      : validationToShowDetails.severity === 'info' ? 'text-blue-800'
                      : 'text-red-800'
                  }`}>
                    {validationToShowDetails.message}
                  </p>
                </div>
              )}

              {/* 1. CONTEXTO DEL FEATURE */}
              {(validationToShowDetails.layerName || validationToShowDetails.fieldName ||
                validationToShowDetails.hallazgo?.id != null || validationToShowDetails.hallazgo?.feature) && (
                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Contexto del Feature</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {validationToShowDetails.layerName && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Capa</label>
                        <p className="mt-1 text-sm font-mono text-gray-900">{validationToShowDetails.layerName}</p>
                      </div>
                    )}
                    {validationToShowDetails.fieldName && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Campo</label>
                        <p className="mt-1 text-sm font-mono text-gray-900">{validationToShowDetails.fieldName}</p>
                      </div>
                    )}
                    {validationToShowDetails.hallazgo?.id != null && validationToShowDetails.hallazgo.id !== -1 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500">ID del Feature (FID)</label>
                        <p className="mt-1 text-sm font-mono text-gray-900">{validationToShowDetails.hallazgo.id}</p>
                      </div>
                    )}
                    {validationToShowDetails.hallazgo?.feature && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Nombre del Feature</label>
                        <p className="mt-1 text-sm text-gray-900">{validationToShowDetails.hallazgo.feature}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 2. DETALLES DE LA VIOLACIÓN */}
              {validationToShowDetails.hallazgo?.fieldValue && (
                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Detalles de la Violación</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {(() => {
                      const decoded = decodeHallazgoFieldValue(
                        validationToShowDetails.hallazgo.fieldValue,
                        validationToShowDetails.type
                      );
                      return decoded.fields.map((field, idx) => {
                        const isWkt = field._key === 'wktEsperado' || field._key === 'wktActual';
                        const isReason = field._key === 'reason' || field._key === 'razon';
                        const isMeta = ['resultadoValidacion', 'advertencia', 'errorDetail'].includes(field._key);
                        const isFullWidth = isWkt || isReason || isMeta;
                        return (
                          <div key={idx} className={isFullWidth ? 'col-span-2' : ''}>
                            <label className={`block text-sm font-medium ${isMeta ? 'text-gray-400' : 'text-gray-500'}`}>
                              {field.label}
                            </label>
                            {isWkt ? (
                              <pre className="mt-1 text-xs font-mono bg-gray-50 border border-gray-200 rounded px-2 py-1 max-h-24 overflow-y-auto whitespace-pre-wrap break-all text-gray-700">
                                {field.value}
                              </pre>
                            ) : (
                              <p className={`mt-1 text-sm break-words ${
                                field._key === 'fieldValue'  ? 'font-mono bg-gray-50 px-2 py-1 rounded border border-gray-200 text-gray-900' :
                                isReason                     ? 'text-gray-700 italic' :
                                isMeta                       ? 'text-gray-400' :
                                'text-gray-900'
                              }`}>
                                {field.renderAs === 'badges'
                                  ? renderBadges(field.value)
                                  : field.value
                                }
                              </p>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

              {/* 3. INFORMACIÓN DE LA REGLA */}
              {(validationToShowDetails.name || validationToShowDetails.type || validationToShowDetails.rule?.operator || validationToShowDetails.severity) && (
                <div className="border-t pt-4">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="text-md font-medium text-gray-900">
                      Información de la Regla
                    </h4>
                    
                    {/* Badge de Estado en el modal */}
                    <span className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${
                      validationToShowDetails.status === 'ok' || validationToShowDetails.status === 'passed'
                        ? 'bg-green-100 text-green-700 border-2 border-green-300'
                        : 'bg-red-100 text-red-700 border-2 border-red-300'
                    }`}>
                      {validationToShowDetails.status === 'ok' || validationToShowDetails.status === 'passed' ? (
                        <>
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          SUPERADA
                        </>
                      ) : (
                        <>
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          NO SUPERADA
                        </>
                      )}
                    </span>
                  </div>
                  
                  {/* Severidad de la Regla - debajo del título */}
                  <div className="flex items-center gap-1 text-xs text-gray-500 mb-3" title="Severidad de la regla">
                    <span className="font-medium">Severidad:</span>
                    <span className={`font-bold text-base mx-1 ${
                      validationToShowDetails.severity === 'error' ? 'text-red-600' :
                      validationToShowDetails.severity === 'warning' ? 'text-yellow-600' :
                      validationToShowDetails.severity === 'info' ? 'text-blue-600' :
                      'text-gray-600'
                    }`}>
                      ♦
                    </span>
                    <span className="font-medium capitalize">
                      {validationToShowDetails.severity === 'error' ? 'Error' :
                       validationToShowDetails.severity === 'warning' ? 'Advertencia' :
                       validationToShowDetails.severity === 'info' ? 'Informativa' :
                       validationToShowDetails.severity}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {validationToShowDetails.name && (
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700">Regla</label>
                        <p className="mt-1 text-sm text-gray-900">{validationToShowDetails.name}</p>
                      </div>
                    )}

                    {validationToShowDetails.type && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Tipo</label>
                        <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize bg-gray-100 text-gray-700`}>
                          {validationTypes.find(t => t.value === validationToShowDetails.type)?.label || validationToShowDetails.type}
                        </span>
                      </div>
                    )}

                    {validationToShowDetails.rule?.operator && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Operador</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {operatorsByType[validationToShowDetails.type]?.find(op => op.value === validationToShowDetails.rule.operator)?.label || validationToShowDetails.rule.operator}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6 pt-4 border-t">
              <BaseButton onClick={() => setShowValidationDetailsModal(false)}>
                Cerrar
              </BaseButton>
            </div>
          </div>
        </div>
      )}
    </div>

  );
};

export default ValidationExecutionModule;